// Settings > Account (admins only): bug reports and suggestions players sent
// from Settings > Support, newest first. Messages are plain text (React escapes them).
import { useEffect, useState } from "react";
import type { FeedbackEntry } from "@wp/shared";
import { api } from "../../net/api";

function when(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function AdminFeedback() {
  const [entries, setEntries] = useState<FeedbackEntry[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  async function load(before: number | null) {
    setBusy(true);
    const res = await api.adminFeedback(before);
    setBusy(false);
    setLoaded(true);
    if (!res.ok) return setError(res.error.message);
    setError(null);
    setEntries((old) => (before === null ? res.value.entries : [...old, ...res.value.entries]));
    setNextBefore(res.value.nextBefore);
  }

  useEffect(() => {
    void load(null);
  }, []);

  return (
    <section className="stack-form group-box admin-feedback" data-testid="admin-feedback">
      <h3>Admin: feedback</h3>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {loaded && !error && entries.length === 0 && <p className="muted small">No bug reports or suggestions yet.</p>}
      {entries.length > 0 && (
        <ul className="feedback-list">
          {entries.map((e) => (
            <li key={e.id} className="feedback-item" data-testid="admin-feedback-item">
              <div className="feedback-meta">
                <span className={e.kind === "bug" ? "feedback-kind bug" : "feedback-kind"}>{e.kind === "bug" ? "Bug" : "Suggestion"}</span>
                <span className="feedback-user">{e.username}</span>
                <time dateTime={new Date(e.createdAt).toISOString()}>{when(e.createdAt)}</time>
              </div>
              <p className="feedback-text">{e.message}</p>
            </li>
          ))}
        </ul>
      )}
      <div className="dialog-actions">
        {nextBefore !== null && (
          <button type="button" className="btn" disabled={busy} onClick={() => void load(nextBefore)} data-testid="admin-feedback-older">
            Load older
          </button>
        )}
        <button type="button" className="btn" disabled={busy} onClick={() => void load(null)} data-testid="admin-feedback-refresh">
          {busy ? "Loading..." : "Refresh"}
        </button>
      </div>
    </section>
  );
}
